import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import {
  Alert,
  Box,
  Checkbox,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'

import T from 'T'
import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { formatCentsToDollars } from 'utils/price'
import { HHNumberField } from 'components/form-fields/v5'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDialogActions from 'components/common/HHDialogActions'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import { useLazyGetSuspensionAutomationQuery, useUpdateSuspensionAutomationMutation } from 'api/accounts/accountBillingSettings'
import { useLazyGetSuspensionAutomationVersionsQuery } from 'api/entity-version/entityVersionCrud'
import { getCustomerDetails } from 'middleware/actions/customers'
import { SUSPENSION_DAYS_LATE_DEFAULT_THRESHOLD } from 'settings/constants/billing'
import useVersionsState from './common/useVersionsState'
import SettingsHistoryAccordion from './common/SettingsHistoryAccordion'
import InheritBillingProfileCheckbox from './common/InheritBillingProfileCheckbox'

const FORM_DEFAULTS = {
  automationEnabled: 'true',
  daysLateEnabled: false,
  daysLate: '',
  suspendWhenBalanceExceedsCreditLimit: false,
  inheritFromBillingProfile: true,
}

const SuspensionAutomationDialog = ({ open = false, onClose = noop, accountId }) => {
  const dispatch = useDispatch()
  const [getSuspensionAutomation, { data, isFetching }] = useLazyGetSuspensionAutomationQuery()
  const [updateSuspensionAutomation, { isLoading: isUpdating }] = useUpdateSuspensionAutomationMutation()
  const [getVersions, { data: versionsData, isLoading: isVersionsLoading, isFetching: isVersionsFetching, isSuccess: isVersionsSuccess }] =
    useLazyGetSuspensionAutomationVersionsQuery()

  const {
    watch,
    setValue,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm({ defaultValues: FORM_DEFAULTS })

  const automationEnabled = watch('automationEnabled')
  const daysLateEnabled = watch('daysLateEnabled')
  const daysLate = watch('daysLate')
  const suspendWhenBalanceExceedsCreditLimit = watch('suspendWhenBalanceExceedsCreditLimit')
  const inheritFromBillingProfile = watch('inheritFromBillingProfile')
  const billingProfile = get(data, 'billingProfile', null)
  const billingProfileName = get(billingProfile, 'name', '')
  const isAutomationOn = automationEnabled === 'true'

  // Blocking banner only applies to values this dialog can actually edit — when inheriting, the
  // fields are read-only and the fix belongs on the billing profile, not here.
  const hasNoActiveCondition = isAutomationOn && !inheritFromBillingProfile && !daysLateEnabled && !suspendWhenBalanceExceedsCreditLimit
  const hasInvalidDaysLateThreshold = isAutomationOn && !inheritFromBillingProfile && daysLateEnabled && !(parseInt(daysLate, 10) >= 1)
  const isInheritedProfileMisconfigured =
    inheritFromBillingProfile &&
    Boolean(billingProfile) &&
    billingProfile.automationEnabled &&
    !billingProfile.daysLateEnabled &&
    !billingProfile.suspendWhenBalanceExceedsCreditLimit

  const { versions, hasVersions, versionsLoading } = useVersionsState({
    versionsData,
    isVersionsLoading,
    isFetching: isVersionsFetching,
    isVersionsSuccess,
  })

  useEffect(() => {
    if (open && accountId) {
      getSuspensionAutomation({ accountId }).unwrap().catch(handleError)
      getVersions({ accountId }).unwrap().catch(handleError)
    }
  }, [open, accountId, getSuspensionAutomation, getVersions])

  useEffect(() => {
    if (data) {
      const inheriting = get(data, 'inheritFromBillingProfile', true)
      const bp = get(data, 'billingProfile')
      const source = inheriting && bp ? bp : data
      reset({
        automationEnabled: String(get(source, 'automationEnabled', true)),
        daysLateEnabled: get(source, 'daysLateEnabled', false),
        daysLate: String(get(source, 'daysLateThreshold', '')),
        suspendWhenBalanceExceedsCreditLimit: get(source, 'suspendWhenBalanceExceedsCreditLimit', false),
        inheritFromBillingProfile: inheriting,
      })
    }
  }, [open, data, reset])

  const handleAutomationChange = useCallback(
    event => {
      setValue('automationEnabled', event.target.value, { shouldDirty: true })
    },
    [setValue]
  )

  const handleDaysLateChange = useCallback(
    values => {
      setValue('daysLate', values.value, { shouldDirty: true })
    },
    [setValue]
  )

  const handleDaysLateEnabledChange = useCallback(
    event => {
      const {
        target: { checked },
      } = event
      setValue('daysLateEnabled', checked, { shouldDirty: true })
      // Prefill only when there is no usable number to preserve. Must be a numeric test: a legacy
      // account loads its stored 0 as the string '0', which is truthy and would skip the prefill.
      if (checked && !(parseInt(getValues('daysLate'), 10) >= 1)) {
        setValue('daysLate', String(SUSPENSION_DAYS_LATE_DEFAULT_THRESHOLD), { shouldDirty: true })
      }
    },
    [setValue, getValues]
  )

  const handleSuspendWhenExceedsCreditLimitChange = useCallback(
    event => {
      setValue('suspendWhenBalanceExceedsCreditLimit', event.target.checked, { shouldDirty: true })
    },
    [setValue]
  )

  const handleInheritChange = useCallback(
    event => {
      const {
        target: { checked },
      } = event
      setValue('inheritFromBillingProfile', checked, { shouldDirty: true })
      if (checked && billingProfile) {
        setValue('automationEnabled', String(get(billingProfile, 'automationEnabled', true)), { shouldDirty: true })
        setValue('daysLateEnabled', get(billingProfile, 'daysLateEnabled', false), { shouldDirty: true })
        setValue('daysLate', String(get(billingProfile, 'daysLateThreshold', '')), { shouldDirty: true })
        setValue('suspendWhenBalanceExceedsCreditLimit', get(billingProfile, 'suspendWhenBalanceExceedsCreditLimit', false), {
          shouldDirty: true,
        })
      }
    },
    [setValue, billingProfile]
  )

  const getVersionChangeLines = useCallback(version => {
    const lines = []
    if ('automationEnabled' in version) {
      lines.push(`Automation: ${version.automationEnabled ? T.ON : T.OFF}`)
    }
    if ('daysLateEnabled' in version) {
      lines.push(`${T.SUSPEND_WHEN_INVOICE_OVERDUE}: ${version.daysLateEnabled ? T.ON : T.OFF}`)
    }
    if ('daysLateThreshold' in version) {
      if (version.daysLateThreshold >= 1) {
        lines.push(`${T.DAYS_LATE}: ${version.daysLateThreshold}`)
      } else if (!('daysLateEnabled' in version)) {
        // Legacy revision predating the flag: a stored 0 meant "rule disabled".
        lines.push(`${T.SUSPEND_WHEN_INVOICE_OVERDUE}: ${T.OFF}`)
      }
    }
    // The rule and the dollar amount are separate settings — label them separately, or a
    // bare "Credit limit" line is ambiguous between "the rule turned on" and "the amount changed".
    if ('suspendWhenBalanceExceedsCreditLimit' in version) {
      lines.push(`${T.SUSPEND_WHEN_BALANCE_EXCEEDS_CREDIT_LIMIT}: ${version.suspendWhenBalanceExceedsCreditLimit ? T.ON : T.OFF}`)
    } else if ('creditLimitCents' in version) {
      const dollars = formatCentsToDollars(version.creditLimitCents)
      lines.push(`${T.CREDIT_LIMIT}: $${Number(dollars).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
    }
    if ('inheritFromBillingProfile' in version) {
      lines.push(`Inherit from profile: ${version.inheritFromBillingProfile ? T.ON : T.OFF}`)
    }
    if (lines.length === 0) {
      lines.push('Settings updated')
    }
    return lines
  }, [])

  const handleUpdate = useCallback(() => {
    const values = getValues()
    const isAutomationEnabled = values.automationEnabled === 'true'

    updateSuspensionAutomation({
      accountId,
      automationEnabled: isAutomationEnabled,
      daysLateEnabled: isAutomationEnabled ? values.daysLateEnabled : false,
      // Sent whenever automation is on, regardless of the days-late checkbox, so unchecking and
      // rechecking restores the number instead of losing it — only the master toggle nulls it.
      daysLateThreshold: isAutomationEnabled ? parseInt(values.daysLate, 10) || null : null,
      suspendWhenBalanceExceedsCreditLimit: values.suspendWhenBalanceExceedsCreditLimit,
      inheritFromBillingProfile: values.inheritFromBillingProfile,
    })
      .unwrap()
      .then(() => {
        toast.success(T.SETTINGS_SAVED_SUCCESSFULLY)
        dispatch(getCustomerDetails({ accountId }))
        onClose()
      })
      .catch(handleError)
  }, [accountId, getValues, updateSuspensionAutomation, onClose])

  return (
    <HHBaseDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <HHDialogTitle title={T.SUSPENSION_AUTOMATION} onClose={onClose} />
      <DialogContent>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {T.SUSPENSION_AUTOMATION_TOOLTIP}
          </Typography>
          <FormControl component="fieldset" sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
            <RadioGroup row value={automationEnabled} onChange={handleAutomationChange}>
              <FormControlLabel
                value="true"
                disabled={inheritFromBillingProfile}
                control={<Radio size="small" />}
                sx={{ width: '50%', mx: 0 }}
                label={
                  <Typography variant="h6" fontWeight="bold" textTransform="uppercase">
                    {T.ON}
                  </Typography>
                }
              />
              <FormControlLabel
                value="false"
                disabled={inheritFromBillingProfile}
                control={<Radio size="small" />}
                sx={{ width: '50%', mx: 0 }}
                label={
                  <Typography variant="h6" fontWeight="bold" textTransform="uppercase">
                    {T.OFF}
                  </Typography>
                }
              />
            </RadioGroup>
          </FormControl>

          {isAutomationOn && (
            <>
              {hasNoActiveCondition && (
                <Alert severity="warning" sx={{ mb: 2, borderColor: 'warning.main', borderWidth: 1, borderStyle: 'solid' }}>
                  <Typography variant="body1">
                    {T.SUSPENSION_AUTOMATION_NO_CONDITIONS_TITLE} <strong>{T.SUSPENSION_AUTOMATION_NO_CONDITIONS_ACTION}</strong>
                  </Typography>
                </Alert>
              )}
              {/* Warning, not info: the consequence is as severe as the local case. Save stays
                  enabled though — the fix lives on the billing profile, so blocking here would
                  trap the user behind read-only controls. */}
              {isInheritedProfileMisconfigured && (
                <Alert severity="warning" sx={{ mb: 2, borderColor: 'warning.main', borderWidth: 1, borderStyle: 'solid' }}>
                  <Typography variant="body1">
                    {T.SUSPENSION_AUTOMATION_PROFILE_NO_CONDITIONS_TITLE}{' '}
                    <strong>{T.SUSPENSION_AUTOMATION_PROFILE_NO_CONDITIONS_ACTION}</strong>
                  </Typography>
                </Alert>
              )}

              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Checkbox
                      checked={daysLateEnabled}
                      onChange={handleDaysLateEnabledChange}
                      disabled={inheritFromBillingProfile}
                      size="small"
                    />
                    <Typography variant="body1">{T.SUSPEND_WHEN_INVOICE_OVERDUE}</Typography>
                    <Tooltip title={T.SUSPEND_WHEN_INVOICE_OVERDUE_TOOLTIP} arrow>
                      <InfoOutlinedIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                  {daysLateEnabled && (
                    <Box ml={4} mt={1} display="flex" alignItems="flex-start" gap={1}>
                      <Box maxWidth={220} flex={1}>
                        <HHNumberField
                          fullWidth
                          deprecatedLabel={false}
                          required
                          disabled={inheritFromBillingProfile}
                          label={T.DAYS_LATE}
                          value={daysLate}
                          onValueChange={handleDaysLateChange}
                          allowNegative={false}
                          decimalScale={0}
                          // undefined is the empty field (allowed while typing); 0 is not — the
                          // rule is disabled by unchecking the box, never by entering 0.
                          isAllowed={({ floatValue }) => floatValue === undefined || (floatValue >= 1 && floatValue <= 999999)}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {T.MINIMUM_ONE_DAY}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box display="flex" alignItems="center" gap={0.5}>
                  <Checkbox
                    checked={suspendWhenBalanceExceedsCreditLimit}
                    onChange={handleSuspendWhenExceedsCreditLimitChange}
                    disabled={inheritFromBillingProfile}
                    size="small"
                  />
                  <Typography variant="body1">{T.SUSPEND_WHEN_BALANCE_EXCEEDS_CREDIT_LIMIT}</Typography>
                  <Tooltip title={T.SUSPEND_CUSTOMERS_EXCEED_CREDIT_LIMIT_TOOLTIP} arrow>
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
              </Stack>
            </>
          )}
          <Divider sx={{ my: 2 }} />
          <InheritBillingProfileCheckbox
            checked={inheritFromBillingProfile}
            onChange={handleInheritChange}
            billingProfileName={billingProfileName}
          >
            {billingProfile && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {T.AUTOMATION}
                  {': '}
                  <strong>{billingProfile.automationEnabled ? T.ON : T.OFF}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {T.SUSPEND_WHEN_INVOICE_OVERDUE}
                  {': '}
                  <strong>{billingProfile.daysLateEnabled ? `${T.ON} (${billingProfile.daysLateThreshold})` : T.OFF}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {T.SUSPEND_WHEN_BALANCE_EXCEEDS_CREDIT_LIMIT}
                  {': '}
                  <strong>{billingProfile.suspendWhenBalanceExceedsCreditLimit ? T.ON : T.OFF}</strong>
                </Typography>
              </>
            )}
          </InheritBillingProfileCheckbox>
        </Paper>
        <SettingsHistoryAccordion
          versions={versions}
          versionsLoading={versionsLoading}
          hasVersions={hasVersions}
          getVersionChangeLines={getVersionChangeLines}
        />
      </DialogContent>
      <HHDialogActions>
        <CancelButton onClick={onClose} />
        <SaveButton
          loading={isUpdating || isFetching}
          disabled={!isDirty || hasNoActiveCondition || hasInvalidDaysLateThreshold}
          onClick={handleUpdate}
          label={T.SAVE}
        />
      </HHDialogActions>
    </HHBaseDialog>
  )
}

SuspensionAutomationDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  accountId: PropTypes.string,
}

export default SuspensionAutomationDialog
